import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTimes,
  FaFileAlt,
  FaTasks,
  FaFlag,
  FaCheck,
  FaExternalLinkAlt,
  FaMagic,
  FaLayerGroup,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import PlantafelService, {
  OperationsTemplate,
  ProjectMilestone,
  ProjectMilestonesResponse,
  MilestoneMapping,
} from '../services/PlantafelService';

interface ApplyTemplateModalProps {
  isOpen: boolean;
  projectId: number;
  projectName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ApplyTemplateModal({
  isOpen,
  projectId,
  projectName,
  onClose,
  onSuccess,
}: ApplyTemplateModalProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<OperationsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<OperationsTemplate | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ jobsCreated: number } | null>(null);
  const [milestonesResponse, setMilestonesResponse] = useState<ProjectMilestonesResponse | null>(null);
  const [milestoneMapping, setMilestoneMapping] = useState<Record<number, number>>({});
  const [quickGroupTarget, setQuickGroupTarget] = useState<number | null>(null);

  // Derived billing milestones from response
  const billingMilestones = milestonesResponse?.milestones || [];

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadBillingMilestones();
      setSelectedTemplateId(null);
      setSelectedTemplate(null);
      setApplyResult(null);
      setMilestoneMapping({});
      setQuickGroupTarget(null);
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (selectedTemplateId) {
      loadTemplateDetails(selectedTemplateId);
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await PlantafelService.fetchTemplates();
      setTemplates(data.filter(t => t.is_active !== false));
    } catch (err) {
      console.error('Failed to load templates:', err);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetails = async (templateId: number) => {
    try {
      const template = await PlantafelService.fetchTemplate(templateId);
      setSelectedTemplate(template);
      setMilestoneMapping({});
    } catch (err) {
      console.error('Failed to load template details:', err);
    }
  };

  const loadBillingMilestones = async () => {
    try {
      // FE-OPS-R27-3: Use new method with positions data
      const response = await PlantafelService.fetchProjectMilestonesWithMeta(projectId, true);
      setMilestonesResponse(response);
    } catch (err) {
      console.error('Failed to load billing milestones:', err);
      setMilestonesResponse(null);
    }
  };

  // FE-OPS-R27-3: Format currency for positions display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // FE-OPS-R27-3: Build dropdown label with positions info
  const getMilestoneOptionLabel = (milestone: ProjectMilestone): string => {
    const baseLabel = `${milestone.label} (${milestone.percent}%)`;
    if (milestone.positions && milestone.positions.length > 0) {
      const posCount = milestone.positions.length;
      const total = milestone.positions_total_net || 0;
      return `${baseLabel} - ${posCount} Leistung${posCount > 1 ? 'en' : ''} / ${formatCurrency(total)}`;
    }
    return baseLabel;
  };

  const handleAutoMatch = () => {
    if (!selectedTemplate?.milestones) return;

    const newMapping: Record<number, number> = {};
    selectedTemplate.milestones.forEach((tm) => {
      const templateLabel = (tm.label || '').toLowerCase().trim();
      const match = billingMilestones.find((bm) =>
        (bm.label || '').toLowerCase().trim() === templateLabel
      );
      if (match && tm.id) {
        newMapping[tm.id] = match.id;
      }
    });
    setMilestoneMapping(newMapping);
    const matchCount = Object.keys(newMapping).length;
    if (matchCount > 0) {
      toast.success(`${matchCount} Milestone(s) automatisch zugeordnet`);
    } else {
      toast.info('Keine passenden Milestones gefunden');
    }
  };

  const handleQuickGroup = () => {
    if (!selectedTemplate?.milestones || !quickGroupTarget) return;

    const newMapping: Record<number, number> = {};
    selectedTemplate.milestones.forEach((tm) => {
      if (tm.id) {
        newMapping[tm.id] = quickGroupTarget;
      }
    });
    setMilestoneMapping(newMapping);
    const targetMilestone = billingMilestones.find((bm) => bm.id === quickGroupTarget);
    toast.success(`Alle Milestones auf "${targetMilestone?.label || '?'}" gesetzt`);
  };

  const milestoneMappingArray = useMemo((): MilestoneMapping[] => {
    return Object.entries(milestoneMapping)
      .filter(([, paymentId]) => paymentId > 0)
      .map(([templateId, paymentId]) => ({
        template_milestone_id: parseInt(templateId, 10),
        payment_milestone_id: paymentId,
      }));
  }, [milestoneMapping]);

  const handleApply = async () => {
    if (!selectedTemplateId) return;

    setApplying(true);
    try {
      const payload: { template_id: number; milestone_mapping?: MilestoneMapping[] } = {
        template_id: selectedTemplateId,
      };
      if (milestoneMappingArray.length > 0) {
        payload.milestone_mapping = milestoneMappingArray;
      }
      const result = await PlantafelService.applyTemplate(projectId, payload);
      setApplyResult({ jobsCreated: result.jobs_created });
      toast.success(`${result.jobs_created} Jobs erstellt`);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to apply template:', err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || 'Fehler beim Anwenden der Vorlage';
      toast.error(errorMsg);
    } finally {
      setApplying(false);
    }
  };

  const handleGoToPlantafel = () => {
    onClose();
    navigate('/operations?tab=plantafel');
  };

  const handleGoToBacklog = () => {
    onClose();
    navigate('/operations?tab=backlog');
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="apply-template-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.navy }}>
              Projektplan aus Vorlage
            </h2>
            {projectName && (
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.textMuted }}>
                Projekt: {projectName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: colors.textMuted,
              borderRadius: '10px',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {applyResult ? (
            // Success State
            <div data-testid="apply-template-success" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: colors.successLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <FaCheck size={28} color={colors.success} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: colors.navy }}>
                Vorlage angewendet!
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: colors.textMuted }}>
                {applyResult.jobsCreated} Jobs wurden erstellt und sind jetzt im Backlog sichtbar.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={handleGoToBacklog}
                  data-testid="go-to-backlog-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    background: colors.bgCard,
                    color: colors.navy,
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <FaTasks size={14} />
                  Zum Backlog
                </button>
                <button
                  onClick={handleGoToPlantafel}
                  data-testid="go-to-plantafel-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: colors.navy,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <FaExternalLinkAlt size={12} />
                  Zur Plantafel
                </button>
              </div>
            </div>
          ) : loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
              Laden...
            </div>
          ) : templates.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
              <FaFileAlt size={40} color={colors.border} style={{ marginBottom: '16px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>
                Keine Vorlagen verfügbar.
              </p>
            </div>
          ) : (
            <>
              {/* Template Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.textMuted, marginBottom: '8px' }}>
                  Vorlage auswählen
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      data-testid={`template-option-${template.code}`}
                      onClick={() => setSelectedTemplateId(template.id)}
                      style={{
                        padding: '12px 20px',
                        border: selectedTemplateId === template.id ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        background: selectedTemplateId === template.id ? colors.infoLight : colors.bgCard,
                        color: colors.navy,
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {template.code}
                    </button>
                  ))}
                </div>
              </div>

              {/* Template Preview */}
              {selectedTemplate && (
                <div
                  data-testid="template-preview"
                  style={{
                    background: colors.bgMuted,
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: 600, color: colors.navy }}>
                    {selectedTemplate.name}
                  </h4>
                  {selectedTemplate.description && (
                    <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: colors.textMuted }}>
                      {selectedTemplate.description}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: '24px' }}>
                    {/* Milestones */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <FaFlag size={12} color={colors.navy} />
                        <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>
                          Milestones ({selectedTemplate.milestones?.length || selectedTemplate.milestones_count || 0})
                        </span>
                      </div>
                      {selectedTemplate.milestones && selectedTemplate.milestones.length > 0 ? (
                        <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '13px', color: colors.textBody }}>
                          {selectedTemplate.milestones.map((m, idx) => (
                            <li key={idx} style={{ marginBottom: '4px' }}>
                              {m.label} ({m.percent}%)
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ margin: 0, fontSize: '13px', color: colors.textLight }}>-</p>
                      )}
                    </div>

                    {/* Jobs */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <FaTasks size={12} color={colors.success} />
                        <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>
                          Jobs ({selectedTemplate.jobs?.length || selectedTemplate.jobs_count || 0})
                        </span>
                      </div>
                      {selectedTemplate.jobs && selectedTemplate.jobs.length > 0 ? (
                        <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '13px', color: colors.textBody, maxHeight: '120px', overflow: 'auto' }}>
                          {selectedTemplate.jobs.map((j, idx) => (
                            <li key={idx} style={{ marginBottom: '4px' }}>
                              {j.title}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ margin: 0, fontSize: '13px', color: colors.textLight }}>-</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Milestone Mapping Section - FE-OPS-R26-1 */}
              {selectedTemplate && billingMilestones.length > 0 && selectedTemplate.milestones && selectedTemplate.milestones.length > 0 && (
                <div
                  data-testid="milestone-mapping-section"
                  style={{
                    marginTop: '20px',
                    background: colors.warningLight,
                    borderRadius: '8px',
                    padding: '20px',
                    border: `1px solid ${colors.warning}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaLayerGroup size={14} color={colors.warning} />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#854d0e' }}>
                        Zuordnung zu Zahlungsplan
                      </span>
                      {milestonesResponse?.offer_id && (
                        <span style={{ fontSize: '11px', color: '#a16207', marginLeft: '4px' }}>
                          (Angebot #{milestonesResponse.offer_id})
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleAutoMatch}
                        data-testid="auto-match-btn"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '10px',
                          background: colors.bgCard,
                          color: colors.textBody,
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        title="Automatisch zuordnen basierend auf Label-Match"
                      >
                        <FaMagic size={10} />
                        Auto-Match
                      </button>
                    </div>
                  </div>

                  {/* Quick Group Row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '10px 12px', background: colors.bgCard, borderRadius: '6px', border: `1px solid ${colors.border}` }}>
                    <span style={{ fontSize: '12px', color: colors.textMuted, whiteSpace: 'nowrap' }}>Alle auf:</span>
                    <select
                      data-testid="quick-group-select"
                      value={quickGroupTarget || ''}
                      onChange={(e) => setQuickGroupTarget(e.target.value ? parseInt(e.target.value, 10) : null)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: colors.textBody,
                        background: colors.bgCard,
                      }}
                    >
                      <option value="">- Milestone wählen -</option>
                      {billingMilestones.map((bm) => (
                        <option key={bm.id} value={bm.id}>
                          {getMilestoneOptionLabel(bm)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleQuickGroup}
                      disabled={!quickGroupTarget}
                      data-testid="quick-group-btn"
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '10px',
                        background: quickGroupTarget ? colors.warning : colors.border,
                        color: quickGroupTarget ? 'white' : colors.textLight,
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: quickGroupTarget ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Setzen
                    </button>
                  </div>

                  {/* Individual Mapping Rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedTemplate.milestones.map((tm) => (
                      <div
                        key={tm.id || tm.label}
                        data-testid={`mapping-row-${tm.id || tm.label}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          background: colors.bgCard,
                          borderRadius: '6px',
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaFlag size={10} color={colors.navy} />
                          <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textBody }}>
                            {tm.label}
                          </span>
                          <span style={{ fontSize: '11px', color: colors.textLight }}>
                            ({tm.percent}%)
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: colors.textLight }}>→</span>
                        <select
                          data-testid={`mapping-select-${tm.id || tm.label}`}
                          value={tm.id ? (milestoneMapping[tm.id] || '') : ''}
                          onChange={(e) => {
                            if (!tm.id) return;
                            const val = e.target.value ? parseInt(e.target.value, 10) : 0;
                            setMilestoneMapping((prev) => ({
                              ...prev,
                              [tm.id!]: val,
                            }));
                          }}
                          style={{
                            width: '280px',
                            padding: '6px 10px',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: colors.textBody,
                            background: colors.bgCard,
                          }}
                        >
                          <option value="">- Nicht zuordnen -</option>
                          {billingMilestones.map((bm) => (
                            <option key={bm.id} value={bm.id}>
                              {getMilestoneOptionLabel(bm)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  {milestoneMappingArray.length > 0 && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: colors.textMuted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaCheck size={10} color={colors.success} />
                      <span>{milestoneMappingArray.length} von {selectedTemplate.milestones.length} Milestones zugeordnet</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!applyResult && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '16px 24px',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <button
              onClick={onClose}
              style={{
                ...ps.buttonSecondary,
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleApply}
              disabled={!selectedTemplateId || applying}
              data-testid="apply-template-btn"
              style={{
                ...(!selectedTemplateId || applying ? ps.buttonDisabled : ps.buttonPrimary),
              }}
            >
              {applying ? 'Wird angewendet...' : 'Anwenden'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
